// This small bootstrap is mirrored in each independent CRA app. Keep copies in sync.
import { browserHeaders, restoreBrowserSession, saveSession, watchBrowserSession } from './browserSession';
export const toolsUrl = () => window.location.hostname === 'localhost' ? 'http://localhost:3003' : 'https://tools.app-breck.com';
const encode = bytes => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

async function exchangeSession(app, tokenKey, apiUrl, code, verifier) {
  const response = await fetch(`${apiUrl}/auth/sso/exchange`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', ...browserHeaders(localStorage.getItem(tokenKey)) },
    body: JSON.stringify({ app, code, verifier }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Sign-in could not be completed. Open the app again from Tools.');
  saveSession(tokenKey, data.token, data.user);
}

async function prepareFrame(app, tokenKey, apiUrl, request) {
  const portal = toolsUrl();
  try {
    const verifier = encode(crypto.getRandomValues(new Uint8Array(32)));
    const challenge = encode(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
    return await new Promise(resolve => {
      let exchanging = false;
      const finish = (ready = false) => { window.clearTimeout(timer); window.removeEventListener('message', receive); resolve(ready); };
      const receive = async event => {
        if (event.origin !== portal || event.source !== window.parent || event.data?.request !== request
            || event.data.type !== 'breck:sso:exchange' || exchanging) return;
        exchanging = true;
        try {
          await exchangeSession(app, tokenKey, apiUrl, event.data.code, verifier);
          window.parent.postMessage({ type: 'breck:sso:ready', request }, portal);
          finish(true);
        } catch (error) {
          window.parent.postMessage({ type: 'breck:sso:error', request, message: error.message }, portal);
          finish();
        }
      };
      const timer = window.setTimeout(finish, 30000);
      window.addEventListener('message', receive);
      window.parent.postMessage({ type: 'breck:sso:challenge', request, challenge }, portal);
    });
  } catch {
    window.parent.postMessage({ type: 'breck:sso:error', request, message: 'Browser sign-in could not start. Please try again.' }, portal);
    return false;
  }
}

async function prepareSso(app, tokenKey, apiUrl) {
  const params = new URLSearchParams(window.location.search);
  if (params.get('breck-login') === 'frame' && window.parent !== window) {
    const ready = await prepareFrame(app, tokenKey, apiUrl, params.get('request'));
    if (!ready || params.get('breck-embed') !== '1') return false;
    window.history.replaceState(null, '', window.location.pathname);
    return true;
  }
  if (params.get('breck-login') === '1') {
    window.history.replaceState(null, '', window.location.pathname);
    try {
      const verifier = encode(crypto.getRandomValues(new Uint8Array(32)));
      sessionStorage.setItem('breck-sso-verifier', verifier);
      const challenge = encode(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
      window.location.replace(`${toolsUrl()}/?app=${app}&challenge=${challenge}`);
      return false;
    } catch {
      sessionStorage.removeItem('breck-sso-verifier');
      sessionStorage.setItem('breck-sso-error', 'Browser sign-in could not start. Sign in here or try again from Tools.');
      return true;
    }
  }
  const code = new URLSearchParams(window.location.hash.slice(1)).get('breck-sso');
  if (!code) return true;
  window.history.replaceState(null, '', window.location.pathname);
  const verifier = sessionStorage.getItem('breck-sso-verifier');
  sessionStorage.removeItem('breck-sso-verifier');
  try {
    if (!verifier) throw new Error('This sign-in was not started in this browser. Open the app again from Tools.');
    await exchangeSession(app, tokenKey, apiUrl, code, verifier);
  } catch (error) {
    sessionStorage.setItem('breck-sso-error', error.message || 'Sign-in could not be completed. Try again from Tools.');
  }
  return true;
}

export async function bootstrapSso(app, tokenKey, apiUrl) {
  try { await restoreBrowserSession(tokenKey, apiUrl); }
  catch { /* An unavailable background check leaves the normal sign-in screen. */ }
  const ready = await prepareSso(app, tokenKey, apiUrl);
  if (ready) watchBrowserSession(tokenKey, apiUrl);
  return ready;
}

export function showSsoError() {
  const message = sessionStorage.getItem('breck-sso-error');
  sessionStorage.removeItem('breck-sso-error');
  if (!message) return;
  const notice = document.createElement('div');
  notice.setAttribute('role', 'alert');
  notice.style.cssText = 'position:relative;z-index:100;background:#fff0dc;color:#713d12;padding:16px;text-align:center;font:14px system-ui';
  notice.append(document.createTextNode(message + ' '));
  const link = document.createElement('a'); link.href = toolsUrl(); link.textContent = 'Return to Tools'; link.style.textDecoration = 'underline'; link.addEventListener('click', returnToTools);
  notice.append(link); document.body.prepend(notice);
}

export function returnToTools(event) {
  if (window.parent !== window) {
    event.preventDefault();
    window.parent.postMessage({ type: 'breck:tools:home' }, toolsUrl());
  }
}
